import React, { useMemo, useRef, useState, useEffect } from "react";
import {
    useTable,
    useGlobalFilter,
    useFilters,
    usePagination,
} from "react-table";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import {
    getAllItemAlquiler,
    insertItemAlquiler,
    updateItemAlquiler,
    deleteItemAlquiler,
} from "../../../api/Alquileres";
import ModalItem from "./ModalItem";

// 🔹 Filtro por defecto
function DefaultColumnFilter({ column: { filterValue, setFilter, placeholder } }) {
    return (
        <input
            className="form-control form-control-sm"
            value={filterValue || ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
            placeholder={placeholder || "Filtrar..."}
            style={{ minWidth: 100 }}
        />
    );
}

export default function AlquilerItems({ refreshTrigger }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const tableRef = useRef(null);

    // 🔹 Cargar ítems
    const loadItems = async () => {
        try {
            setLoading(true);
            const res = await getAllItemAlquiler();

            if (Array.isArray(res)) {
                const normalizados = res.map((it) => ({
                    ...it,
                    coditem: it.coditem ?? "",
                    item: it.item ?? "",
                    proveedor: it.proveedor ?? "",
                    estado: it.estado ?? "",
                    esComprado: it.esComprado ?? "NO",
                    esAlquilado: it.esAlquilado ?? "NO",
                    fechadev:
                        it.fechadev ??
                        it.fechadevolucion ??
                        it.fechaDevolucion ??
                        it.fecha_devolucion ??
                        null,
                }));

                const ordenados = normalizados.sort((a, b) => {
                    const aAlq = String(a.esAlquilado || "").toUpperCase() === "SI" ? 1 : 0;
                    const bAlq = String(b.esAlquilado || "").toUpperCase() === "SI" ? 1 : 0;
                    const aComp = String(a.esComprado || "").toUpperCase() === "SI" ? 1 : 0;
                    const bComp = String(b.esComprado || "").toUpperCase() === "SI" ? 1 : 0;

                    if (aAlq !== bAlq) return bAlq - aAlq;
                    if (aComp !== bComp) return bComp - aComp;
                    return (a.item || "").localeCompare(b.item || "");
                });

                setData(ordenados);
            } else {
                setData([]);
            }
        } catch (err) {
            Swal.fire("Error", err.message || "Error al cargar ítems", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadItems(); }, []);
    useEffect(() => { loadItems(); }, [refreshTrigger]);

    // 🔹 Guardar
    const handleSave = async (formData) => {
        try {
            if (selectedItem) {
                await updateItemAlquiler(selectedItem.iditemalquiler, {
                    ...formData,
                    useredicion: "admin",
                });
                Swal.fire("Actualizado", "Ítem actualizado correctamente", "success");
            } else {
                await insertItemAlquiler({
                    ...formData,
                    usercreacion: "admin",
                });
                Swal.fire("Agregado", "Ítem agregado correctamente", "success");
            }
            setShowModal(false);
            setSelectedItem(null);
            loadItems();
        } catch (err) {
            Swal.fire("Error", err.message || "No se pudo guardar el ítem", "error");
        }
    };

    // 🔹 Eliminar ítem
    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "¿Eliminar ítem?",
            text: "Esta acción no se puede deshacer.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        });

        if (confirm.isConfirmed) {
            try {
                await deleteItemAlquiler(id);
                Swal.fire("Eliminado", "Ítem eliminado correctamente", "success");
                loadItems();
            } catch (err) {
                Swal.fire("Error", err.message || "No se pudo eliminar", "error");
            }
        }
    };

    // helpers de fecha
    const parseDateOnly = (dateStr) => {
        if (!dateStr) return null;
        let datePart = String(dateStr).split("T")[0].trim();
        if (datePart.includes("-")) {
            const parts = datePart.split("-");
            if (parts.length < 3) return null;
            const [y, m, d] = parts.map((v) => parseInt(v, 10));
            if ([y, m, d].some((n) => Number.isNaN(n))) return null;
            return new Date(y, m - 1, d);
        } else if (datePart.includes("/")) {
            const parts = datePart.split("/");
            if (parts.length < 3) return null;
            const [d, m, y] = parts.map((v) => parseInt(v, 10));
            if ([y, m, d].some((n) => Number.isNaN(n))) return null;
            return new Date(y, m - 1, d);
        }
        const nums = datePart.replace(/\D/g, "");
        if (nums.length === 8) {
            const y = parseInt(nums.slice(0, 4), 10);
            const m = parseInt(nums.slice(4, 6), 10);
            const d = parseInt(nums.slice(6, 8), 10);
            if ([y, m, d].some((n) => Number.isNaN(n))) return null;
            return new Date(y, m - 1, d);
        }
        return null;
    };

    const daysUntil = (dateStr) => {
        const target = parseDateOnly(dateStr);
        if (!target) return null;
        const today = new Date();
        const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const msPerDay = 24 * 60 * 60 * 1000;
        return Math.ceil((target.getTime() - todayMid.getTime()) / msPerDay);
    };

    const formatDateLocal = (dateStr) => {
        const d = parseDateOnly(dateStr);
        if (!d) return "-";
        return d.toLocaleDateString();
    };

    // 🔹 Columnas
    const columns = useMemo(
        () => [
            {
                Header: "ID",
                accessor: "iditemalquiler",
                className: "text-center col-id",
                minWidth: 60,
                maxWidth: 90,
            },
            {
                Header: "Código",
                accessor: "coditem",
                className: "text-center col-cod",
                minWidth: 80,
                maxWidth: 120,
            },
            {
                Header: "Ítem",
                accessor: "item",
                minWidth: 200,
                Cell: ({ value }) => (
                    <div style={{ whiteSpace: "normal", wordBreak: "break-word", textAlign: "left" }}>
                        {value ?? ""}
                    </div>
                ),
            },
            {
                Header: "Proveedor",
                accessor: "proveedor",
                minWidth: 180,
                Cell: ({ value }) => (
                    <div style={{ whiteSpace: "normal", wordBreak: "break-word", textAlign: "left" }}>
                        {value ?? ""}
                    </div>
                ),
            },
            {
                Header: "Tipo",
                accessor: "tipo",
                className: "text-center",
                minWidth: 120,
                Cell: ({ row }) => {
                    const { esComprado, esAlquilado } = row.original;
                    const comprado = String(esComprado || "").toUpperCase() === "SI";
                    const alquilado = String(esAlquilado || "").toUpperCase() === "SI";
                    if (comprado) return <span className="badge bg-primary" style={{ fontSize: "0.85rem", fontWeight: 600 }}>COMPRADO</span>;
                    if (alquilado) return <span className="badge bg-warning" style={{ fontSize: "0.85rem", fontWeight: 600 }}>ALQUILADO</span>;
                    return <span className="badge bg-secondary" style={{ fontSize: "0.85rem", fontWeight: 500 }}>-</span>;
                },
            },
            {
                Header: "Fecha Devolución",
                accessor: "fechadev",
                className: "text-center col-fecha-dev",
                minWidth: 140,
                Cell: ({ row, value }) => {
                    const original = row.original || {};
                    const esAlquilado = String(original.esAlquilado || "").toUpperCase() === "SI";
                    const fecha = value || original.fechadev || null;
                    const dias = daysUntil(fecha);
                    const isUrgent = esAlquilado && dias !== null && dias <= 5;
                    const isOverdue = esAlquilado && dias !== null && dias < 0;
                    const displayDate = formatDateLocal(fecha);

                    let daysText = "";
                    if (dias === null) daysText = "";
                    else if (dias > 0) daysText = `( ${dias} día${dias > 1 ? "s" : ""})`;
                    else if (dias === 0) daysText = "(Hoy)";
                    else daysText = `(vencido ${Math.abs(dias)} día${Math.abs(dias) > 1 ? "s" : ""})`;

                    const [fechaTxt, diasTxt] = daysText ? [displayDate, daysText] : [displayDate, ""];
                    const styleBase = { whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: "1.3" };

                    if (!esAlquilado) return <div style={{ ...styleBase, color: "#6c757d" }}>-</div>;
                    if (isUrgent || isOverdue) {
                        return (
                            <div style={{ ...styleBase, color: "#dc3545", fontWeight: 600 }}>
                                {fechaTxt}<br />{diasTxt}
                            </div>
                        );
                    }
                    return (
                        <div style={styleBase}>
                            {fechaTxt}
                            {diasTxt && <><br />{diasTxt}</>}
                        </div>
                    );
                },
            },
            {
                Header: "Estado",
                accessor: "estado",
                className: "text-center",
                minWidth: 100,
                Cell: ({ value }) => {
                    const txt = (value ?? "").toString().trim();
                    const disponible = txt.toLowerCase() === "disponible";
                    return (
                        <span className={`badge ${disponible ? "bg-success" : "bg-danger"}`} style={{ fontSize: 12 }}>
                            {txt || "-"}
                        </span>
                    );
                },
            },
            {
                Header: "Opciones",
                accessor: "acciones",
                disableFilters: true,
                disableGlobalFilter: true,
                className: "text-center options",
                width: 120,
                // ✅ Todos pueden editar
                Cell: ({ row }) => (
                    <div className="text-center">
                        <button
                            className="btn btn-outline-primary btn-sm me-1"
                            onClick={() => {
                                setSelectedItem(row.original);
                                setShowModal(true);
                            }}
                            title="Editar"
                        >
                            <i className="fas fa-pen"></i>
                        </button>
                    </div>
                ),
            },
        ],
        []
    );

    const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

    const tableInstance = useTable(
        { columns, data, defaultColumn, initialState: { pageSize: 15, pageIndex: 0 } },
        useFilters,
        useGlobalFilter,
        usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        state,
        page,
        rows,
        gotoPage,
        pageCount,
        pageOptions,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        setGlobalFilter,
    } = tableInstance;

    const { globalFilter, pageIndex } = state;

    const handleExportExcel = () => {
        if (!data.length)
            return Swal.fire("Sin datos", "No hay ítems para exportar", "info");

        const mapped = data.map((it) => ({
            Código: it.coditem,
            Ítem: it.item,
            Proveedor: it.proveedor,
            Tipo: it.esAlquilado === "SI" ? "ALQUILADO" : it.esComprado === "SI" ? "COMPRADO" : "-",
            Estado: it.estado,
            "Fecha Devolución": it.esAlquilado === "SI" ? formatDateLocal(it.fechadev) : "-",
        }));

        const ws = XLSX.utils.json_to_sheet(mapped);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "ItemsAlquiler");
        XLSX.writeFile(wb, `ItemsAlquiler.xlsx`);
    };

    return (
        <div className="mt-3">
            <style>{`
        .tabla-scroll {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #fff;
        }
        .tabla-items {
          table-layout: auto;
          width: 100%;
          min-width: 1000px;
          font-size: 0.92rem;
          border-collapse: separate;
          border-spacing: 0;
        }
        .tabla-items thead tr.header-row th {
          position: sticky;
          top: 0;
          z-index: 3;
          background: #1569c9ff;
          color: #fff;
          font-weight: 600;
          border-bottom: none !important;
        }
        .tabla-items thead tr.filter-row th {
          position: sticky;
          top: 42px;
          z-index: 2;
          background: #eaf3ff;
          border-bottom: 1px solid #d6e4ff !important;
        }
        .tabla-items tbody tr:nth-child(even) { background: #fafafa; }
        .tabla-items tbody tr:hover { background: #f1f5f9; }
        .tabla-items th, .tabla-items td {
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: middle;
          padding: 8px 10px !important;
          border-color: #eef2f7 !important;
          height: 40px;
          text-align: left;
          min-width: 80px;
        }
        .text-center { text-align: center !important; }
        .col-id { width: 6%; min-width: 60px; max-width: 90px; }
        .col-cod { width: 8%; min-width: 80px; max-width: 120px; }
        .col-fecha-dev { width: 12%; min-width: 120px; max-width: 180px; }
        .tabla-items th.text-center.options, .tabla-items td.text-center.options {
          width: 10%;
          min-width: 120px;
        }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

            {/* 🔹 Encabezado y acciones */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex gap-2">
                    {/* ✅ Agregar siempre visible */}
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            setSelectedItem(null);
                            setShowModal(true);
                        }}
                    >
                        + Agregar Ítem
                    </button>
                    <button
                        className="btn btn-outline-success"
                        onClick={handleExportExcel}
                        disabled={loading || !rows || rows.length === 0}
                    >
                        Exportar Excel
                    </button>
                </div>
                <div style={{ width: 250 }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar en todo…"
                        value={globalFilter || ""}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                </div>
            </div>

            {/* 🔹 Spinner / Tabla */}
            {loading ? (
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "30vh" }}>
                    <div style={{ width: 38, height: 38, border: "4px solid #ddd", borderTop: "4px solid #1569c9ff", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                    <span style={{ marginLeft: 14, color: "#444", fontSize: 18 }}>Cargando ítems...</span>
                </div>
            ) : (
                <div className="tabla-scroll">
                    <table {...getTableProps()} className="table table-sm tabla-items" ref={tableRef}>
                        <thead>
                            {headerGroups.map((headerGroup, i) => (
                                <React.Fragment key={i}>
                                    <tr {...headerGroup.getHeaderGroupProps()} className="header-row">
                                        {headerGroup.headers.map((column) => (
                                            <th key={column.id} {...column.getHeaderProps({ className: column.className })}>
                                                {column.render("Header")}
                                            </th>
                                        ))}
                                    </tr>
                                    <tr className="filter-row">
                                        {headerGroups[i].headers.map((column) => (
                                            <th key={`${column.id}-filter`} {...column.getHeaderProps({ className: column.className })}>
                                                {column.canFilter ? column.render("Filter") : null}
                                            </th>
                                        ))}
                                    </tr>
                                </React.Fragment>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {page.map((row, ri) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} key={ri}>
                                        {row.cells.map((cell, ci) => (
                                            <td {...cell.getCellProps({ className: cell.column.className })} key={ci}>
                                                {cell.render("Cell")}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                            {page.length < 15 &&
                                Array.from({ length: 15 - page.length }).map((_, i) => (
                                    <tr key={`empty-${i}`}>
                                        <td colSpan={columns.length}>&nbsp;</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 🔹 Paginación */}
            <div className="d-flex justify-content-between mt-2 align-items-center">
                <span>Página <strong>{pageIndex + 1} de {pageOptions.length || 1}</strong></span>
                <div>
                    <button className="btn btn-outline-primary btn-sm me-1" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>
                    <button className="btn btn-outline-primary btn-sm me-1" onClick={() => previousPage()} disabled={!canPreviousPage}>Anterior</button>
                    <button className="btn btn-outline-primary btn-sm me-1" onClick={() => nextPage()} disabled={!canNextPage}>Siguiente</button>
                    <button className="btn btn-outline-primary btn-sm" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>
                </div>
            </div>

            {/* 🔹 Modal Agregar/Editar — siempre editable */}
            <ModalItem
                show={showModal}
                onHide={() => { setShowModal(false); setSelectedItem(null); }}
                onSave={handleSave}
                itemData={selectedItem}
                viewOnly={false}
            />
        </div>
    );
}